import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, X, Loader2, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/skills`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const Stars = ({ value = 0, onChange, testid }) => (
  <span className="inline-flex items-center gap-0.5" data-testid={testid}>
    {[1, 2, 3, 4, 5].map(n => (
      <button key={n} type="button" onClick={() => onChange(n === value ? 0 : n)} data-testid={testid ? `${testid}-${n}` : undefined} className="hover:scale-110 transition-transform">
        <Star className={`w-5 h-5 ${n <= value ? 'fill-amber-400 text-amber-500' : 'text-slate-300'}`} />
      </button>
    ))}
  </span>
);

// PM rates how each crew member did on the skills the job needed → MASON nudges the office to adjust stars.
export function RateJobModal({ req, onClose }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/feedback`, { params: { request_id: req.id }, ...auth() }).then(r => {
      setData(r.data);
      const f = {};
      r.data.people.forEach(p => { f[p.id] = { skills: { ...(p.feedback?.skills || {}) }, overall: p.feedback?.overall || 0, note: p.feedback?.note || '' }; });
      setForm(f);
    }).catch(e => { toast.error(e.response?.data?.detail || 'Could not load the crew'); onClose(); });
  }, [req.id]); // eslint-disable-line
  if (!data) return null;
  const set = (pid, patch) => setForm(f => ({ ...f, [pid]: { ...f[pid], ...patch } }));
  const save = async () => {
    setBusy(true);
    let n = 0, nudges = 0;
    try {
      for (const p of data.people) {
        const f = form[p.id];
        const skills = Object.fromEntries(Object.entries(f.skills).filter(([, v]) => v > 0));
        if (!Object.keys(skills).length && !f.overall) continue;
        const r = await axios.post(`${API}/feedback`, { request_id: req.id, person_id: p.id, skills, overall: f.overall || null, note: f.note }, auth());
        n += 1; nudges += (r.data.nudges || []).length;
      }
      toast.success(n ? `Rated ${n} ${n === 1 ? 'person' : 'people'}${nudges ? ` — MASON has ${nudges} star nudge${nudges > 1 ? 's' : ''} for the office` : ''}` : 'Nothing to save');
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3" data-testid="rate-job-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto">
        <div className="flex items-start justify-between gap-3 p-4 border-b border-slate-100">
          <div>
            <p className="text-base font-black text-slate-900 inline-flex items-center gap-1.5"><Sparkles className="w-4 h-4" /> How did it go?</p>
            <p className="text-xs text-slate-500">{data.request.request_number} · {data.request.work_order_number} {data.request.customer_name} · {data.request.start_date}{data.request.end_date && data.request.end_date !== data.request.start_date ? ` → ${data.request.end_date}` : ''}. Your scores nudge each guy's stars; the office confirms.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="rate-job-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-4">
          {data.people.length === 0 && <p className="text-sm text-slate-500">No named crew on this request yet.</p>}
          {data.people.map(p => (
            <div key={p.id} className="rounded-xl border border-slate-200 p-3" data-testid={`rate-person-${p.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-bold text-slate-900">{p.name}</p>
                <label className="text-xs text-slate-600 inline-flex items-center gap-2">Overall <Stars value={form[p.id]?.overall || 0} onChange={v => set(p.id, { overall: v })} testid={`rate-overall-${p.id}`} /></label>
              </div>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                {[...p.skills, ...Object.keys(form[p.id]?.skills || {}).filter(k => !p.skills.some(s => s.key === k)).map(k => ({ key: k, name: data.skill_names[k] || k, current: 0 }))].map(s => (
                  <div key={s.key} className="flex items-center justify-between gap-2 py-0.5">
                    <span className="text-xs text-slate-700">{s.name}{s.current ? <span className="text-slate-400"> · now {s.current}★</span> : <span className="text-slate-400"> · unrated</span>}</span>
                    <Stars value={form[p.id]?.skills?.[s.key] || 0} onChange={v => set(p.id, { skills: { ...form[p.id].skills, [s.key]: v } })} testid={`rate-${p.id}-${s.key}`} />
                  </div>
                ))}
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <select value="" onChange={e => { if (e.target.value) set(p.id, { skills: { ...form[p.id].skills, [e.target.value]: form[p.id].skills[e.target.value] || 0 } }); }} className="h-7 px-2 rounded-lg border border-slate-300 text-[11px] text-slate-600 bg-white" data-testid={`rate-add-skill-${p.id}`}>
                  <option value="">+ rate another skill…</option>
                  {Object.entries(data.skill_names).filter(([k]) => !p.skills.some(s => s.key === k) && !(k in (form[p.id]?.skills || {}))).map(([k, n]) => <option key={k} value={k}>{n}</option>)}
                </select>
                <input value={form[p.id]?.note || ''} onChange={e => set(p.id, { note: e.target.value })} placeholder="Anything worth remembering? (optional)" className="flex-1 min-w-[200px] h-7 px-2 rounded-lg border border-slate-300 text-xs" data-testid={`rate-note-${p.id}`} />
              </div>
            </div>
          ))}
        </div>
        <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="h-9 px-3 rounded-lg border border-slate-300 text-sm font-semibold" data-testid="rate-job-cancel">Cancel</button>
          <button onClick={save} disabled={busy || !data.people.length} className="h-9 px-4 rounded-lg bg-slate-900 text-white text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="rate-job-save">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save ratings
          </button>
        </div>
      </div>
    </div>
  );
}

// Office admins: MASON's suggested star changes for one person, from PM job feedback.
export function NudgePanel({ userId, nudges, onChanged }) {
  const [busy, setBusy] = useState('');
  if (!nudges?.length) return null;
  const act = async (n, apply) => {
    setBusy(n.skill);
    try {
      const r = apply
        ? await axios.post(`${API}/nudges/${userId}/apply`, { skill: n.skill, stars: n.suggested }, auth())
        : await axios.post(`${API}/nudges/${userId}/dismiss`, { skill: n.skill, fingerprint: n.fingerprint }, auth());
      toast.success(apply ? `${n.name} → ${n.suggested}★` : 'Dismissed');
      onChanged(r.data.nudges);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); } finally { setBusy(''); }
  };
  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-3" data-testid="nudge-panel">
      <p className="text-sm font-bold text-indigo-950 inline-flex items-center gap-1.5"><Sparkles className="w-4 h-4" /> MASON suggests ({nudges.length})</p>
      <p className="text-[11px] text-indigo-900/70">From how PMs rated this person's recent jobs. Apply changes the stars; dismiss hides it until new feedback comes in.</p>
      <ul className="mt-2 space-y-1.5">
        {nudges.map(n => (
          <li key={n.skill} className="flex flex-wrap items-center gap-2 rounded-lg bg-white border border-indigo-100 px-2.5 py-1.5" data-testid={`nudge-${n.skill}`}>
            <span className="text-sm text-slate-900 font-semibold">{n.name}</span>
            <span className={`text-xs font-bold ${n.direction === 'up' ? 'text-emerald-700' : 'text-rose-700'}`}>{n.current ? `${n.current}★` : 'unrated'} → {n.suggested}★</span>
            <span className="text-[11px] text-slate-500 flex-1" title={n.evidence.map(e => `${e.date} ${e.wo || ''} ${e.customer || ''}: ${e.score}★ by ${e.by}${e.note ? ` — ${e.note}` : ''}`).join('\n')}>
              {n.n} PM rating{n.n > 1 ? 's' : ''}, avg {n.avg} · latest {n.evidence[0].wo || n.evidence[0].customer || ''} by {n.evidence[0].by}{n.evidence[0].note ? ` — “${n.evidence[0].note}”` : ''}
            </span>
            <button onClick={() => act(n, true)} disabled={busy === n.skill} className="h-7 px-2.5 rounded-lg bg-indigo-600 text-white text-[11px] font-bold disabled:opacity-40" data-testid={`nudge-apply-${n.skill}`}>Apply {n.suggested}★</button>
            <button onClick={() => act(n, false)} disabled={busy === n.skill} className="h-7 px-2 rounded-lg border border-slate-300 text-[11px] font-semibold disabled:opacity-40" data-testid={`nudge-dismiss-${n.skill}`}>Dismiss</button>
          </li>
        ))}
      </ul>
    </div>
  );
}
